/**
 * ч.1093: перевірка розпізнаного вхідного документа (Oblik+/AI).
 *
 * Ліворуч — сам файл (PDF/фото у браузері; для Excel/Word — витягнутий
 * текст), праворуч — те, що розпізнано і зіставлено з довідниками:
 * постачальник, дата/номер, рядки з номенклатурою. Кожен рядок позначено,
 * ЯК його знайдено (словник постачальника / артикул / назва / схоже), не
 * зіставлені — червоним: бухгалтер обирає картку або створює нову, і вибір
 * запамʼятовується для цього постачальника. Кнопка створює ЧЕРНЕТКУ
 * прибуткової накладної або рахунку постачальника — проведення окремо.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import {
  KIND_LABELS, STATUS_LABELS, recognition,
  type ConfirmLine, type ConfirmPayload, type IncomingDocument, type ResolvedLine, type Target,
} from '@/api/recognition';
import { usePermissions } from '@/auth/usePermissions';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import { extractApiError } from '@/utils/extractApiError';
import DocStatusBadge, { type BadgeTone } from '@/shared/components/DocStatusBadge';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import QuickNomenclatureModal, { type QuickNomenclatureInitial } from '@/shared/components/QuickNomenclatureModal';
import TchTable from '@/shared/components/TchTable';

const STATUS_TONE: Record<string, BadgeTone> = {
  QUEUED: 'gray', RECOGNIZING: 'blue', RECOGNIZED: 'amber', CONFIRMED: 'green', REJECTED: 'gray', FAILED: 'red',
};
const MATCH_LABEL: Record<string, string> = {
  dictionary: 'зі словника постачальника', article: 'за артикулом', name: 'за назвою',
  similar: 'схожа назва — перевірте',
};
const muted = { color: 'var(--text-secondary)', fontSize: '0.85rem' } as const;
const fmt2 = (v: number | string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export interface LineState {
  line_no: number;
  supplier_name: string;
  supplier_article: string | null;
  unit: string | null;
  nomenclature_id: string;
  suggested_nomenclature_id: string | null;
  match: string | null;
  card_unit: string | null;
  unit_mismatch: boolean;
  candidates: ResolvedLine['candidates'];
  is_service: boolean;
  quantity: string;
  price: string;
  vat_rate_id: string;
  uom_id: string | null;
}

export interface HeaderState {
  target: Target;
  contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  document_date: string;
  supplier_doc_number: string;
  prices_include_vat: boolean;
  payment_due_date: string;
  comment: string;
  /** ч.1096: вхідна ПН до створюваної накладної ('' — без привʼязки). */
  tax_invoice_id: string;
}

/** Та сама арифметика, що на backend (matching.line_amounts). */
export function lineTotals(qty: string, price: string, ratePct: number, includeVat: boolean) {
  const q = Number(qty) || 0;
  const p = Number(price) || 0;
  const base = Math.round(q * p * 100) / 100;
  if (includeVat) {
    const without = ratePct ? Math.round((base / (1 + ratePct / 100)) * 100) / 100 : base;
    return { without, vat: Math.round((base - without) * 100) / 100, total: base };
  }
  const vat = Math.round(base * ratePct) / 100;
  return { without: base, vat, total: Math.round((base + vat) * 100) / 100 };
}

export function initialState(doc: IncomingDocument): { header: HeaderState; lines: LineState[] } | null {
  const r = doc.resolved;
  if (!r) return null;
  return {
    header: {
      target: r.target,
      contractor_id: r.contractor.id ?? '',
      contract_id: '',
      warehouse_id: r.warehouse_id ?? '',
      document_date: r.date ?? new Date().toISOString().slice(0, 10),
      supplier_doc_number: r.number ?? '',
      prices_include_vat: r.prices_include_vat,
      payment_due_date: r.payment_due_date ?? '',
      comment: '',
      tax_invoice_id: r.tax_invoice?.status === 'found' ? (r.tax_invoice.candidates[0]?.id ?? '') : '',
    },
    lines: r.lines.map((l) => ({
      line_no: l.line_no, supplier_name: l.name, supplier_article: l.article, unit: l.unit,
      nomenclature_id: l.nomenclature_id ?? '', suggested_nomenclature_id: l.nomenclature_id,
      match: l.match, candidates: l.candidates, is_service: l.is_service,
      card_unit: l.card_unit, unit_mismatch: l.unit_mismatch,
      quantity: l.quantity, price: l.price, vat_rate_id: l.vat_rate_id ?? '', uom_id: l.uom_id,
    })),
  };
}

/** ч.1275: що підставити в попап «нова номенклатура» з рядка документа. Ставку ПДВ беремо лише
 * ненульову: 20 % чи 7 % — властивість товару, а 0 / «без ПДВ» у постачальника зазвичай означає,
 * що він не платник ПДВ, — у картку це переносити не можна. */
export function nomenclatureInitialFromLine(
  l: LineState, vatPercent: (vatRateId: string) => number,
): QuickNomenclatureInitial {
  return {
    name: l.supplier_name,
    article: l.supplier_article,
    vatRateId: l.vat_rate_id && vatPercent(l.vat_rate_id) > 0 ? l.vat_rate_id : null,
    isService: l.is_service,
    baseUomId: l.uom_id,
    unitLabel: l.unit,
  };
}

export function buildPayload(header: HeaderState, lines: LineState[]): ConfirmPayload {
  const out: ConfirmLine[] = lines.map((l, i) => ({
    line_no: i + 1,
    nomenclature_id: l.nomenclature_id || null,
    description: l.is_service && !l.nomenclature_id ? l.supplier_name : null,
    is_service: l.is_service,
    quantity: l.quantity, price: l.price, vat_rate_id: l.vat_rate_id,
    uom_id: l.uom_id, supplier_name: l.supplier_name, supplier_article: l.supplier_article,
    suggested_nomenclature_id: l.suggested_nomenclature_id, remember: true,
  }));
  return {
    target: header.target, contractor_id: header.contractor_id,
    contract_id: header.contract_id || null,
    warehouse_id: header.warehouse_id || null,
    document_date: header.document_date,
    supplier_doc_number: header.supplier_doc_number || null,
    prices_include_vat: header.prices_include_vat,
    payment_due_date: header.payment_due_date || null,
    comment: header.comment || null,
    tax_invoice_id: header.target === 'GOODS_RECEIPT' && header.tax_invoice_id ? header.tax_invoice_id : null,
    lines: out,
  };
}

function FilePreview({ doc }: { doc: IncomingDocument }) {
  const [url, setUrl] = useState<string | null>(null);
  const isPdf = doc.mime_type === 'application/pdf';
  const isImage = doc.mime_type.startsWith('image/');
  useEffect(() => {
    if (!isPdf && !isImage) return;
    let obj: string | null = null;
    recognition.fileBlob(doc.id).then((b) => { obj = URL.createObjectURL(b); setUrl(obj); }).catch(() => setUrl(null));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [doc.id, isPdf, isImage]);
  if (isPdf) {
    return url ? <iframe title="Файл документа" src={url} style={{ width: '100%', height: '78vh', border: '1px solid var(--border)', borderRadius: 8 }} />
      : <div style={muted}>Завантаження файлу…</div>;
  }
  if (isImage) {
    return url ? <img src={url} alt={doc.filename} style={{ maxWidth: '100%', border: '1px solid var(--border)', borderRadius: 8 }} />
      : <div style={muted}>Завантаження файлу…</div>;
  }
  return (
    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', maxHeight: '78vh', overflow: 'auto',
      background: 'var(--bg-soft)', padding: '0.75rem', borderRadius: 8 }}>{doc.source_text ?? '(текст не витягнуто)'}</pre>
  );
}

export default function IncomingDocumentReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const { has } = usePermissions();
  const canConfirm = has('recognition.confirm');
  const canUpload = has('recognition.upload');

  const docQ = useQuery({
    queryKey: ['incoming-document', id],
    queryFn: () => recognition.get(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && ['QUEUED', 'RECOGNIZING'].includes(q.state.data.status) ? 4000 : false),
  });
  const doc = docQ.data;
  const orgId = doc?.organization_id ?? '';

  const [header, setHeader] = useState<HeaderState | null>(null);
  const [lines, setLines] = useState<LineState[]>([]);
  const [initFor, setInitFor] = useState<string | null>(null);
  useEffect(() => {
    if (!doc?.resolved || initFor === doc.id) return;
    const st = initialState(doc);
    if (st) { setHeader(st.header); setLines(st.lines); setInitFor(doc.id); }
  }, [doc, initFor]);

  const ctrQ = useQuery({ queryKey: ['contractors-full'], queryFn: () => contractorsApi.listAll(), enabled: !!doc });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll(), enabled: !!doc });
  const vatQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates, staleTime: 300_000 });
  const whQ = useQuery({ queryKey: ['warehouses', orgId], queryFn: () => refs.warehouses(orgId), enabled: !!orgId });
  const contractsQ = useQuery({
    queryKey: ['contracts', orgId, header?.contractor_id],
    queryFn: () => refs.contracts({ organization_id: orgId, contractor_id: header!.contractor_id }),
    enabled: !!orgId && !!header?.contractor_id,
  });

  const [newContractor, setNewContractor] = useState(false);
  const [newNomFor, setNewNomFor] = useState<number | null>(null);

  const ctrItems = useMemo(() => (ctrQ.data?.items ?? [])
    .filter((c) => !c.deletion_mark)
    .map((c) => ({ id: c.id, label: c.name, hint: c.edrpou || c.inn || null })), [ctrQ.data]);
  const nomItems = useMemo(() => (nomQ.data?.items ?? [])
    .filter((n) => !n.deletion_mark && !n.is_group)
    .map((n) => ({ id: n.id, label: n.name, hint: n.article || n.code || null })), [nomQ.data]);
  const vatPct = (vatId: string) => Number(vatQ.data?.find((v) => v.id === vatId)?.rate_percent ?? 0);

  const totals = useMemo(() => lines.reduce((acc, l) => {
    const t = lineTotals(l.quantity, l.price, vatPct(l.vat_rate_id), header?.prices_include_vat ?? false);
    return { without: acc.without + t.without, vat: acc.vat + t.vat, total: acc.total + t.total };
  }, { without: 0, vat: 0, total: 0 }), [lines, header?.prices_include_vat, vatQ.data]);  // eslint-disable-line react-hooks/exhaustive-deps

  const confirmM = useMutation({
    mutationFn: () => recognition.confirm(doc!.id, buildPayload(header!, lines)),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['incoming-documents'] });
      qc.invalidateQueries({ queryKey: ['incoming-document', id] });
      toast.show({ type: 'success', message: `Створено чернетку ${res.document_number}. Перевірте і проведіть.` });
      navigate(res.document_type === 'doc.goods_receipts'
        ? `/goods-receipts/${res.document_id}` : `/supplier-invoices/${res.document_id}`);
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося створити документ') }),
  });
  const rejectM = useMutation({
    mutationFn: () => recognition.reject(doc!.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['incoming-documents'] }); navigate('/incoming-documents'); },
  });
  const retryM = useMutation({
    mutationFn: () => recognition.retry(doc!.id),
    onSuccess: () => { setInitFor(null); qc.invalidateQueries({ queryKey: ['incoming-document', id] }); },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });

  if (docQ.isLoading || !doc) return <div className="card">Завантаження…</div>;
  const r = doc.resolved;
  const editable = doc.status === 'RECOGNIZED' && canConfirm;
  const hasGoods = lines.some((l) => !l.is_service);
  const blockers: string[] = [];
  if (header && !header.contractor_id) blockers.push('оберіть постачальника');
  if (header?.target === 'GOODS_RECEIPT' && hasGoods && !header.warehouse_id) blockers.push('оберіть склад');
  if (lines.length === 0) blockers.push('немає рядків');
  if (lines.some((l) => !l.is_service && !l.nomenclature_id)) blockers.push('є товарні рядки без номенклатури');
  if (header?.target === 'SUPPLIER_INVOICE' && lines.some((l) => !l.nomenclature_id)) blockers.push('для рахунку кожен рядок потребує номенклатури');
  if (lines.some((l) => !l.vat_rate_id)) blockers.push('вкажіть ставку ПДВ у кожному рядку');

  const setLine = (i: number, patch: Partial<LineState>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Вхідний документ: {doc.filename}</h2>
        <DocStatusBadge label={STATUS_LABELS[doc.status]} tone={STATUS_TONE[doc.status] ?? 'gray'} />
        {doc.doc_kind && <span style={muted}>{KIND_LABELS[doc.doc_kind]}{doc.confidence ? ` · впевненість ${Math.round(Number(doc.confidence) * 100)}%` : ''}</span>}
        <span style={{ marginLeft: 'auto' }}><Link to="/incoming-documents">← до списку</Link></span>
      </div>

      {doc.status === 'CONFIRMED' && doc.created_document_id && (
        <div className="account__tfa-hint" style={{ marginBottom: '0.75rem' }}>
          З цього файлу створено документ:{' '}
          <Link to={doc.created_document_type === 'doc.goods_receipts'
            ? `/goods-receipts/${doc.created_document_id}` : `/supplier-invoices/${doc.created_document_id}`}>
            відкрити
          </Link>
        </div>
      )}
      {doc.status === 'SPLIT' && (
        <div className="account__tfa-hint" style={{ marginBottom: '0.75rem' }}>
          У файлі знайдено кілька документів ({doc.children.length}) — кожен винесено окремим рядком на полку:
          <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>
            {doc.children.map((c) => (
              <li key={c.id}>
                <Link to={`/incoming-documents/${c.id}`}>{c.filename}</Link>
                {' — '}{c.doc_kind ? KIND_LABELS[c.doc_kind] : 'документ'}
                {c.doc_number ? ` №${c.doc_number}` : ''}{c.supplier_name ? `, ${c.supplier_name}` : ''}
                {' · '}{STATUS_LABELS[c.status]}
              </li>
            ))}
          </ul>
        </div>
      )}
      {doc.status === 'FAILED' && (
        <div className="error" style={{ marginBottom: '0.75rem' }}>
          {doc.error}
          {canUpload && <> {' '}<button type="button" onClick={() => retryM.mutate()} disabled={retryM.isPending}>розпізнати знову</button></>}
        </div>
      )}
      {['QUEUED', 'RECOGNIZING'].includes(doc.status) && (
        <div style={{ ...muted, marginBottom: '0.75rem' }}>Oblik+/AI читає документ — зазвичай до хвилини. Сторінка оновиться сама.</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)', gap: '1rem' }}>
        <div><FilePreview doc={doc} /></div>
        <div>
          {r && header && (
            <>
              {r.checks.length > 0 && (
                <div style={{ background: 'var(--badge-amber-bg, #fef3c7)', border: '1px solid #f59e0b', borderRadius: 8,
                  padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                  <b>Перевірте:</b>
                  <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>
                    {r.checks.map((c, i) => (
                      <li key={i} style={c.level === 'error' ? { color: 'var(--danger)' }
                        : c.level === 'info' ? { color: 'var(--text-secondary)' } : undefined}>{c.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              {r.notes && <div style={{ ...muted, marginBottom: '0.5rem' }}>Примітка Oblik+/AI: {r.notes}</div>}
              {r.ai?.escalated && (
                <div style={{ ...muted, marginBottom: '0.5rem' }} data-testid="ai-escalation">
                  Документ перечитано точнішим розпізнаванням ({r.ai.reasons.join('; ')}).
                  Списано за обидві спроби: {r.ai.attempts.map((a) => fmt2(a.credits)).join(' + ')} кредитів.
                </div>
              )}
              {r.ai?.note && <div style={{ ...muted, marginBottom: '0.5rem' }}>{r.ai.note}</div>}

              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Створити</label>
                  <select value={header.target} disabled={!editable}
                    onChange={(e) => setHeader({ ...header, target: e.target.value as Target })}>
                    <option value="GOODS_RECEIPT">прибуткову накладну (надходження)</option>
                    <option value="SUPPLIER_INVOICE">рахунок постачальника</option>
                  </select>
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Дата документа</label>
                  <DatePopover value={header.document_date} disabled={!editable}
                    onChange={(v) => setHeader({ ...header, document_date: v })} />
                </div>
                <div className="field" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    Постачальник
                    {r.contractor.match && <span style={muted}> · знайдено {r.contractor.match === 'code' ? 'за кодом' : 'за назвою'}</span>}
                    {!r.contractor.id && <span style={{ color: 'var(--danger)' }}> · не знайдено у довіднику</span>}
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SearchSelect items={ctrItems} value={header.contractor_id} disabled={!editable}
                        placeholder="— оберіть постачальника —"
                        onChange={(v) => setHeader({ ...header, contractor_id: v, contract_id: '' })} />
                    </div>
                    {editable && (
                      <button type="button" onClick={() => setNewContractor(true)}>створити з реквізитів</button>
                    )}
                  </div>
                  <div style={muted}>
                    У документі: {r.contractor.from_document.name ?? '—'}
                    {r.contractor.from_document.edrpou ? `, ЄДРПОУ ${r.contractor.from_document.edrpou}` : ''}
                    {r.contractor.from_document.ipn ? `, ІПН/РНОКПП ${r.contractor.from_document.ipn}` : ''}
                    {r.contractor.candidates.length > 0 && !r.contractor.id && (
                      <> · схожі: {r.contractor.candidates.map((c) => (
                        <button key={c.id} type="button" className="sup__shot" onClick={() => setHeader({ ...header, contractor_id: c.id })}>{c.name}</button>
                      ))}</>
                    )}
                  </div>
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>№ документа постачальника</label>
                  <input value={header.supplier_doc_number} disabled={!editable}
                    onChange={(e) => setHeader({ ...header, supplier_doc_number: e.target.value })} />
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Договір</label>
                  <select value={header.contract_id} disabled={!editable || !header.contractor_id}
                    onChange={(e) => setHeader({ ...header, contract_id: e.target.value })}>
                    <option value="">— за замовчуванням —</option>
                    {(contractsQ.data ?? []).map((c: { id: string; name: string }) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {header.target === 'GOODS_RECEIPT' ? (
                  <div className="field" style={{ gridColumn: 'span 6' }}>
                    <label>Склад{hasGoods ? ' (обовʼязково для товарів)' : ''}</label>
                    <select value={header.warehouse_id} disabled={!editable}
                      onChange={(e) => setHeader({ ...header, warehouse_id: e.target.value })}>
                      <option value="">— оберіть —</option>
                      {(whQ.data ?? []).map((w: { id: string; name: string }) => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                ) : (
                  <div className="field" style={{ gridColumn: 'span 6' }}>
                    <label>Оплатити до</label>
                    <DatePopover value={header.payment_due_date} disabled={!editable}
                      onChange={(v) => setHeader({ ...header, payment_due_date: v })} />
                  </div>
                )}
                <div className="field" style={{ gridColumn: 'span 6', alignSelf: 'end' }}>
                  <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <input type="checkbox" style={{ width: 'auto' }} checked={header.prices_include_vat} disabled={!editable}
                      onChange={(e) => setHeader({ ...header, prices_include_vat: e.target.checked })} />
                    ціни в документі з ПДВ
                  </label>
                </div>
              </div>

              {header.target === 'GOODS_RECEIPT' && r.tax_invoice && r.tax_invoice.status !== 'not_applicable' && (
                <div style={{ marginTop: '0.6rem', fontSize: '0.85rem' }} data-testid="pn-block">
                  <b>Податкова накладна:</b>{' '}
                  {r.tax_invoice.status === 'not_found' && (
                    <span style={muted}>у реєстрі ще немає ПН на цю суму від постачальника — зʼявиться після отримання з ЄРПН.</span>
                  )}
                  {r.tax_invoice.status === 'linked_elsewhere' && (
                    <span style={{ color: '#b45309' }}>
                      ПН №{r.tax_invoice.candidates[0]?.number} від {r.tax_invoice.candidates[0]?.date} вже привʼязана до
                      {' '}{r.tax_invoice.candidates[0]?.linked_to ?? 'іншого документа'} — перевірте, чи не вносили цю накладну раніше.
                    </span>
                  )}
                  {(r.tax_invoice.status === 'found' || r.tax_invoice.status === 'ambiguous') && (
                    <span style={{ display: 'inline-flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      {r.tax_invoice.candidates.filter((c) => !c.linked).map((c) => (
                        <label key={c.id} style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                          <input type="radio" name="pn" style={{ width: 'auto' }} disabled={!editable}
                            checked={header.tax_invoice_id === c.id}
                            onChange={() => setHeader({ ...header, tax_invoice_id: c.id })} />
                          №{c.number} від {new Date(c.date).toLocaleDateString('uk-UA')} на {fmt2(c.total)}
                          {c.registered ? '' : ' (не зареєстрована)'}
                        </label>
                      ))}
                      <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                        <input type="radio" name="pn" style={{ width: 'auto' }} disabled={!editable}
                          checked={!header.tax_invoice_id}
                          onChange={() => setHeader({ ...header, tax_invoice_id: '' })} />
                        без привʼязки
                      </label>
                      <span style={muted}>— привʼяжеться до створеної накладної</span>
                    </span>
                  )}
                </div>
              )}

              <TchTable style={{ marginTop: '0.75rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>#</th>
                    <th>У документі</th>
                    <th style={{ width: '30%' }}>Номенклатура</th>
                    <th style={{ width: 44 }} title="Послуга">посл.</th>
                    <th style={{ width: 70 }}>К-сть</th>
                    <th style={{ width: 90 }}>Ціна</th>
                    <th style={{ width: 84 }}>ПДВ</th>
                    <th style={{ width: 100, textAlign: 'right' }}>Разом</th>
                    <th style={{ width: 28 }} />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const t = lineTotals(l.quantity, l.price, vatPct(l.vat_rate_id), header.prices_include_vat);
                    const unmatched = !l.nomenclature_id && !l.is_service;
                    return (
                      <tr key={i} style={unmatched ? { background: 'var(--badge-red-bg, #fee2e2)' } : undefined}>
                        <td>{i + 1}</td>
                        <td>
                          <div>{l.supplier_name}{l.supplier_article ? <span style={muted}> · арт. {l.supplier_article}</span> : null}</div>
                          <div style={{ ...muted, fontSize: '0.72rem' }}>
                            {l.unit ? `${l.unit} · ` : ''}
                            {l.match ? MATCH_LABEL[l.match] : (l.is_service ? 'послуга без картки — рядком «Послуги»' : 'не зіставлено')}
                            {l.unit_mismatch && (
                              <span style={{ color: '#b45309', fontWeight: 700 }}
                                title="Одиниця в документі відрізняється від базової одиниці картки — перерахуйте кількість і ціну">
                                {' '}⚠ у картці: {l.card_unit}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <SearchSelect items={nomItems} value={l.nomenclature_id} disabled={!editable}
                                placeholder="— оберіть картку —"
                                onChange={(v) => {
                                  const nom = nomQ.data?.items.find((n) => n.id === v);
                                  setLine(i, { nomenclature_id: v, is_service: nom ? !!nom.is_service : l.is_service,
                                    uom_id: nom?.base_uom_id ?? l.uom_id });
                                }} />
                            </div>
                            {editable && <button type="button" title="Створити нову картку" onClick={() => setNewNomFor(i)}>нова</button>}
                          </div>
                          {l.candidates.length > 0 && !l.nomenclature_id && (
                            <div style={{ ...muted, fontSize: '0.72rem' }}>
                              схожі: {l.candidates.slice(0, 3).map((c) => (
                                <button key={c.id} type="button" className="sup__shot" onClick={() => setLine(i, { nomenclature_id: c.id, is_service: !!c.is_service })}>{c.name}</button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" style={{ width: 'auto' }} checked={l.is_service} disabled={!editable}
                            onChange={(e) => setLine(i, { is_service: e.target.checked })} />
                        </td>
                        <td><input value={l.quantity} disabled={!editable} inputMode="decimal"
                          onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                        <td><input value={l.price} disabled={!editable} inputMode="decimal"
                          onChange={(e) => setLine(i, { price: e.target.value })} /></td>
                        <td>
                          <select value={l.vat_rate_id} disabled={!editable} onChange={(e) => setLine(i, { vat_rate_id: e.target.value })}>
                            <option value="">—</option>
                            {(vatQ.data ?? []).filter((v) => v.is_active || v.id === l.vat_rate_id).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                          </select>
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt2(t.total)}</td>
                        <td>{editable && <button type="button" className="sup__shot" title="Прибрати рядок"
                          onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>×</button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'right' }}>
                      без ПДВ {fmt2(totals.without)} · ПДВ {fmt2(totals.vat)} · разом
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmt2(totals.total)}</td>
                    <td />
                  </tr>
                  {r.totals.document_total && Math.abs(Number(r.totals.document_total) - totals.total) > 0.05 && (
                    <tr><td colSpan={9} style={{ color: '#b45309', fontWeight: 400 }}>
                      У документі «Разом» {fmt2(r.totals.document_total)} — не збігається з рядками.
                    </td></tr>
                  )}
                </tfoot>
              </TchTable>

              {editable && (
                <div className="field" style={{ marginTop: '0.5rem' }}>
                  <label>Коментар до документа</label>
                  <input value={header.comment} onChange={(e) => setHeader({ ...header, comment: e.target.value })} />
                </div>
              )}

              <div className="sup__actions" style={{ alignItems: 'center' }}>
                {blockers.length > 0 && editable && <span style={{ ...muted, marginRight: 'auto' }}>Щоб створити: {blockers.join('; ')}.</span>}
                {canConfirm && doc.status !== 'CONFIRMED' && (
                  <button type="button" onClick={async () => {
                    if (await confirmDlg({ message: 'Відхилити цей документ? Він лишиться на полці як «відхилено».' })) rejectM.mutate();
                  }}>відхилити</button>
                )}
                {canUpload && doc.status === 'RECOGNIZED' && (
                  <button type="button" onClick={() => retryM.mutate()} disabled={retryM.isPending}>розпізнати знову</button>
                )}
                {editable && (
                  <button type="button" className="primary" disabled={blockers.length > 0 || confirmM.isPending}
                    title={blockers.length ? blockers.join('; ') : 'Створюється чернетка — проведете її самі'}
                    onClick={() => confirmM.mutate()}>
                    {confirmM.isPending ? 'створюю…' : header.target === 'GOODS_RECEIPT' ? 'створити прибуткову накладну' : 'створити рахунок постачальника'}
                  </button>
                )}
              </div>
            </>
          )}
          {!r && !['QUEUED', 'RECOGNIZING', 'FAILED'].includes(doc.status) && <div style={muted}>Немає результату розпізнавання.</div>}
        </div>
      </div>

      {newContractor && (
        <QuickContractorModal direction="OUT" onClose={() => setNewContractor(false)}
          initial={r ? {
            name: r.contractor.from_document.name, edrpou: r.contractor.from_document.edrpou,
            ipn: r.contractor.from_document.ipn, address: r.contractor.from_document.address,
          } : undefined}
          onCreated={(cid) => { setNewContractor(false); qc.invalidateQueries({ queryKey: ['contractors-full'] }); if (header) setHeader({ ...header, contractor_id: cid }); }} />
      )}
      {newNomFor !== null && (
        <QuickNomenclatureModal onClose={() => setNewNomFor(null)}
          initial={lines[newNomFor] ? nomenclatureInitialFromLine(lines[newNomFor], vatPct) : undefined}
          onCreated={(nom) => {
            qc.invalidateQueries({ queryKey: ['nomenclature-full'] });
            // одиниця рядка — з картки, а якщо в картці її немає, лишається з документа
            setLine(newNomFor, { nomenclature_id: nom.id, is_service: !!nom.is_service,
              uom_id: nom.base_uom_id ?? lines[newNomFor]?.uom_id ?? null });
            setNewNomFor(null);
          }} />
      )}
    </div>
  );
}
